import {Sprite} from "../../Core/Sprite";
import {array, Callback, Mix} from "../../Core/Reflection";
import {TransformBackup} from "../../Core/TransformBackup";
import {MusicType} from "./MusicModel";
import {Poly} from "../../Core/Shape";
import {MusicNotesBurst} from "./MusicNotesBurst";
import {AbstractMusicActivity} from "./AbstractMusicActivity";
import {glockenspiel} from "../../Layout/MusicScreenLayout";
import {AudioChannel} from "../../Core/AudioChannel";
import {Keyboard} from "../Keyboard";
import {Focusable} from "../Focusable";
import {KeyboardFocusLayer} from "../KeyboardFocusLayer";
import {Signal} from "../../Core/Signal";
import {HomeScreen} from "../../States/HomeScreen";

const reactionVO = [
    "LetsGoLuna_197",
    "LetsGoLuna_198"
];

export class GlockenspielActivity extends AbstractMusicActivity<Glockenspiel> {

    public readonly keysHitAreas = array(() => []);
    public readonly keys: GlockenspielKey[] = [];

    constructor(screen: HomeScreen) {
        super(screen);

        glockenspiel.call(this);

        this.keys.push(...this.keysHitAreas.map((it, i) => this.container.addChild(new GlockenspielKey(i + 1, it))));
        this.keys.forEach(it => {
            it.visible = false;
            it.onPlayNote.add(() => this.playReactionVO());
        });
    }

    protected getSprite(): Glockenspiel {
        return new Glockenspiel();
    }

    protected getMusicType(): MusicType {
        return MusicType.Glockenspiel;
    }

    protected activate() {
        super.activate();
        this.keys.forEach(it => it.visible = true);
    }

    protected deactivate() {
        this.keys.forEach(it => it.visible = false);
        super.deactivate();
    }

    protected initKeyboard() {
        Keyboard.add(
            ...this.keys,
            this.buttonOk
        );
    }

    protected getReactionVO(): string[] | undefined {
        return reactionVO;
    }

    protected getFactVO(): string | undefined {
        return "LetsGoLuna_199";
    }
}

class GlockenspielKey extends Mix(Poly, Focusable) {

    public readonly index: number;
    private static pointerDownOnKey = false;
    private static bgmVolume?: number;
    private static bgmTimeout?: number;
    public readonly onPlayNote = Signal<Callback>();

    constructor(index: number, points: number[]) {
        super(points);

        this.index = index;
        this.interactive = true;
        this.buttonMode = true;
        this.alpha = 0;

        this.on("pointerdown", () => {
            GlockenspielKey.pointerDownOnKey = true;
            this.playNote();
        });
        this.on("pointerup", () => {
            GlockenspielKey.pointerDownOnKey = false;
        });
        this.on("pointerupoutside", () => {
            GlockenspielKey.pointerDownOnKey = false;
        });
        this.on("pointerover", () => {
            if (GlockenspielKey.pointerDownOnKey) {
                this.playNote();
            }
        });
    }

    private playNote() {
        if (GlockenspielKey.bgmVolume === undefined) {
            GlockenspielKey.bgmVolume = AudioChannel.BGM.volume;
        }
        AudioChannel.BGM.volume = 0;
        window.clearTimeout(GlockenspielKey.bgmTimeout);
        GlockenspielKey.bgmTimeout = window.setTimeout(() => AudioChannel.BGM.volume = GlockenspielKey.bgmVolume ?? 1, 2000);
        AudioChannel.SFX.play(`${MusicType.Glockenspiel}-${this.index}`);
        const notes = this.parent.addChild(new MusicNotesBurst(this.width * 0.5, this.height * 0.8));
        notes.x = this.x + this.center;
        notes.y = this.y + this.middle;
        notes.emitter.playOnce(() => {
            notes.destroy();
        });
        this.onPlayNote();
    }

    destroy(options?: { children?: boolean; texture?: boolean; baseTexture?: boolean }) {
        this.onPlayNote.remove();
        super.destroy(options);
    }

    onFocusActivated() {
        if (this.worldVisible) {
            this.emit("pointerdown");
            this.emit("pointerup");
        }
    }

    onFocusBegin() {
        KeyboardFocusLayer.instance.currentFocusable = this;
        this.emit("pointerdown");
    }

    onFocusEnd() {
        KeyboardFocusLayer.instance.currentFocusable = undefined;
        this.emit("pointerup");
    }
}

class Glockenspiel extends Mix(Sprite, TransformBackup) {
}